import { create, el } from "@dhx/ts-common/dom";
import { locateNode } from "@dhx/ts-common/html";
import { IHandlers } from "@dhx/ts-common/types";
import { View } from "@dhx/ts-common/view";
import { Position, tooltip } from "@dhx/ts-message";
import { Popup, PopupEvents } from "@dhx/ts-popup";
import { Key } from "../keyhandler";
import locale from "../locales/en";
import { Editor } from "../models/Editor";
import { Action, IRichTextEventSystem, Modifier, RichTextEvents } from "../types";
import { getTextHash } from "./other";

interface ILinkEditorConfig {
	events: IRichTextEventSystem;
	editor: Editor;
	getRootView: () => void;
}

export class LinkEditor extends View {
	private _events: IRichTextEventSystem;
	private _editor: Editor;
	private _getParentView: () => any;

	private _popup: Popup;
	private _handlers: IHandlers;

	private _editMode: boolean;

	private _initText: string;

	private _link: string;
	private _text: string;

	private _currentLinkRef: string;

	constructor(container, config: ILinkEditorConfig) {
		super(container, config);

		this._events = config.events;
		this._editor = config.editor;
		this._getParentView = config.getRootView;

		this._popup = new Popup();

		this._initEvents();
		this._handlers = {
			onclick: {
				".dhx_richtext-linkeditor--handler_apply": () => this._apply(),
				".dhx_richtext-linkeditor--handler_unlink, .dhx_richtext-linkeditor--handler_unlink *": () => {
					this._events.fire(RichTextEvents.action, [
						Action.update,
						{
							modifier: Modifier.link,
							modifierValue: undefined,
						},
						true,
					]);
					this._popup.hide();
				},
				".dhx_richtext-linkeditor--handler_edit, .dhx_richtext-linkeditor--handler_edit *": () => {
					this._editMode = true;
					this.paint();
					setTimeout(() => this.focus(), 10);
				},
			},
			onmouseover: e => {
				const node = locateNode(e, "dhx_id") as HTMLElement;
				if (node) {
					tooltip(locale[node.getAttribute("dhx_id")], {
						node,
						position: Position.bottom,
					});
				}
			},
			oninput: e => {
				const value = e.target.value;
				if (e.target.classList.contains("link-input")) {
					if (!this._link && value) {
						this._link = value;
						this.paint();
					} else if (!value) {
						this._link = value;
						this.paint();
					} else {
						this._link = value;
					}
				} else {
					this._text = value;
				}
			},
			onkeydown: e => {
				if (e.which === Key.enter && this._link) {
					this._apply();
				}
				if (e.which === Key.esc) {
					this._popup.hide();
				}
			},
		};

		this.mount(null, create({ render: () => this._draw() }));

		this._popup.attach(this);
	}
	isVisible() {
		return this._popup.isVisible();
	}
	show(node: HTMLElement, mode: any) {
		this._popup.show(node, { mode, centering: true });
		setTimeout(() => this.focus(), 10);
	}
	hide() {
		this._popup.hide();
	}
	get() {
		return {
			link: this._link,
			text: this._text,
		};
	}
	getCurrentLinkRef() {
		return this._currentLinkRef;
	}
	set(link: string, text: string) {
		this._editMode = !link;

		this._link = link || "";
		this._text = text || "";
		this._initText = this._text;
		this.paint();
	}
	focus() {
		const refs = this.getRootView().refs;
		if (refs && refs.linkInput) {
			refs.linkInput.el.focus();
		}
	}
	private _destroyCurrentLinkRef() {
		if (this._currentLinkRef) {
			this._getParentView().refs[this._currentLinkRef].patch({ class: "" });
			this._currentLinkRef = null;
		}
	}
	private _initEvents() {
		this._popup.events.on(
			PopupEvents.beforeHide,
			(outer, e) => !outer || !this._getParentView().node.el.contains(e.target)
		);
		this._popup.events.on(PopupEvents.afterHide, () => this._destroyCurrentLinkRef());
		this._popup.events.on(PopupEvents.afterShow, () => {
			const left = this._editor.selection.get().left;
			const key = getTextHash(left.blockIndex, left.textIndex);
			const parentView = this._getParentView();
			const vnode = parentView.refs[key];
			vnode.parent.patch({ class: "dhx--link_edited" });
			this._currentLinkRef = vnode.parent.ref;
		});
		this._events.on(RichTextEvents.selectionChange, ({ range, left }) => {
			const textNode = this._editor.blocks[left.blockIndex].textNodes[left.textIndex];
			if (this.isVisible()) {
				this.hide();
			}
			if (textNode.style.link && !range) {
				this._show();
			}
		});

		this._events.on(RichTextEvents.showLinkEditor, () => this._show());

		this._events.on(RichTextEvents.change, () => {
			if (this.isVisible()) {
				const { left, range } = this._editor.selection.get();
				const textNode = this._editor.blocks[left.blockIndex].textNodes[left.textIndex];

				if (!textNode.style.link || range) {
					this.hide();
				} else {
					const { link, text } = this._editor.getLinkInfo();
					this.set(link, text);
				}
			}
		});
	}
	private _draw() {
		return el(
			`.dhx_richtext-linkeditor ${this._drawViewMode() ? ".dhx_richtext-linkeditor--mode_view" : ""}`,
			{
				...this._handlers,
			},
			this._editMode ? this._drawEditMode() : this._drawViewMode()
		);
	}
	private _drawEditMode() {
		return [
			el(".dhx_richtext-linkeditor__inputs", [
				el(".dhx_form-group.dhx_form-group--type_inline.dhx_richtext-linkeditor__input", [
					el(".dhx_label", locale.text),
					el("input.dhx_input.text-input", {
						value: this._text,
						_key: "textInput",
						_ref: "textInput",
					}),
				]),
				el(".dhx_form-group.dhx_form-group--type_inline.dhx_richtext-linkeditor__input", [
					el(".dhx_label", locale.link),
					el("input.dhx_input.link-input", {
						value: this._link,
						_key: "linkInput",
						_ref: "linkInput",
					}),
				]),
			]),
			el(".dhx_richtext-linkeditor__controls", [
				el(
					"button.dhx_richtext-linkeditor--handler_apply.dhx_button.dhx_button--size_medium.dhx_button--view_flat.dhx_button--color_primary",
					{
						disabled: !this._link,
					},
					locale.apply
				),
			]),
		];
	}
	private _drawViewMode() {
		return [
			el("a.dhx_richtext-linkeditor__link", { href: this._link, target: "_blank" }, this._link),
			el(".dhx_richtext-linkeditor__controls", [
				el(
					"button.dhx_richtext-linkeditor--handler_edit.dhx_button.dhx_button--icon.dhx_button--size_medium.dhx_button--view_link.dhx_button--color_secondary",
					{
						dhx_id: "edit",
					},
					[el("span.dhx_button__icon.dxi.dxi-pencil")]
				),
				el(
					"button.dhx_richtext-linkeditor--handler_unlink.dhx_button.dhx_button--icon.dhx_button--size_medium.dhx_button--view_link.dhx_button--color_secondary",
					{
						dhx_id: "removeLink",
					},
					[el("span.dhx_button__icon.dxi.dxi-link-variant-off")]
				),
			]),
		];
	}
	private _modifyLink(value: string) {
		if (value.startsWith("#")) {
			return value;
		}
		if (value.slice(0, 4).toLowerCase() === "http") {
			return value;
		}

		return "http://" + value;
	}

	private _apply() {
		const link = this._modifyLink(this._link);
		const text = this._text;

		const isEmptyText = !text.trim();
		const isTextChanged = this._initText !== this._text;
		if (isTextChanged || isEmptyText) {
			this._editor.updateLinkText(isEmptyText ? link : text);
		}
		if (link) {
			this._events.fire(RichTextEvents.action, [
				Action.update,
				{
					modifier: Modifier.link,
					modifierValue: link,
				},
				true,
			]);
		}
		this.hide();
	}
	private _show() {
		const { link, text } = this._editor.getLinkInfo();
		const left = this._editor.selection.get().left;
		const key = getTextHash(left.blockIndex, left.textIndex);
		const parentView = this._getParentView();
		const vnode = parentView.refs[key];

		if (!link) {
			// case for add new link
			this.set(null, this._editor.selection.getSelectedText());
			this.show(vnode.el, Position.bottom);
		} else {
			// link already exists
			if (vnode.parent.ref === this._currentLinkRef) {
				// this link already in focus
				return;
			}
			this.set(link, text);
			this.show(vnode.parent.el, Position.bottom);
		}
	}
}
